import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Breakpoints, Color, Grid, Shadow, Type } from '../app/StyleGuide';

const TOAST_EXIT_DURATION = 180;

export type AlertToastType = 'success' | 'warning' | 'error' | 'info' | 'custom' | 'danger';

type ToastVariant = 'success' | 'warning' | 'error' | 'info' | 'custom';

type VariantStyle = {
  background: string;
  border: string;
  accent: string;
  strong: string;
};

const VARIANT_STYLES: Record<ToastVariant, VariantStyle> = {
  success: {
    background: Color.Primary._05,
    border: Color.Primary._20,
    accent: Color.Primary._50,
    strong: Color.Primary._70,
  },
  error: {
    background: Color.Red._05,
    border: Color.Red._20,
    accent: Color.Red._50,
    strong: Color.Red._70,
  },
  warning: {
    background: Color.Yellow._05,
    border: Color.Yellow._20,
    accent: Color.Yellow._50,
    strong: Color.Yellow._80,
  },
  info: {
    background: Color.Blue._10,
    border: Color.Blue._20,
    accent: Color.Blue._50,
    strong: Color.Blue._60,
  },
  custom: {
    background: Color.Secondary._05,
    border: Color.Secondary._20,
    accent: Color.Secondary._50,
    strong: Color.Secondary._80,
  },
};

export function resolveToastVariant(type: AlertToastType): ToastVariant {
  if (type === 'danger') return 'error';
  return type;
}

function toastIconClassName(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'fas fa-check-circle';
    case 'error':
      return 'fas fa-times-circle';
    case 'warning':
      return 'fas fa-exclamation-triangle';
    case 'info':
    case 'custom':
    default:
      return 'fas fa-info-circle';
  }
}

/** Matches `NavbarContainer` max width at large breakpoints. */
const NAVBAR_CONTENT_WIDTH_PX = 1170;

const toastEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, -12px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const toastExit = keyframes`
  from {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  to {
    opacity: 0;
    transform: translate3d(0, -12px, 0);
  }
`;

const ViewportAnchored = styled.div`
  position: relative;
  margin: auto;
  width: 100%;
  max-width: ${NAVBAR_CONTENT_WIDTH_PX}px;
`;

const ViewportAlign = styled.div`
  position: absolute;
  top: ${Grid._3};
  right: 0;
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
`;

/** Newest toast first (top). Older items overlap upward with a slight scale so they peek below. */
export const ToastStackColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  pointer-events: none;

  @media (min-width: ${Breakpoints.screen_sm}) {
    align-items: flex-end;
  }
`;

export const ToastStackSlot = styled.div<{ $stackIndex: number; $stackSize: number }>`
  pointer-events: none;
  position: relative;
  flex-shrink: 0;
  z-index: ${({ $stackIndex, $stackSize }) => 100 + ($stackSize - $stackIndex)};
  margin-top: ${({ $stackIndex }) => ($stackIndex === 0 ? 0 : -50)}px;
  transform: ${({ $stackIndex }) => {
    if ($stackIndex === 0) return 'scale(1)';
    const s = Math.max(0.86, 1 - $stackIndex * 0.045);
    return `scale(${s})`;
  }};
  transform-origin: bottom center;
`;

const Banner = styled.div<{ $isExiting: boolean; $variant: ToastVariant }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${Grid._3};
  min-height: 56px;
  min-width: 240px;
  padding: ${Grid._3};
  overflow: hidden;
  box-sizing: border-box;
  border-radius: ${Grid._4};
  border: 1px solid ${({ $variant }) => VARIANT_STYLES[$variant].border};
  box-shadow: ${Shadow.medium};
  font-family: ${Type.family};
  font-size: ${Type.Scale._2};
  line-height: ${Type.Leading.tall};
  text-transform: none;
  color: ${Type.Color.dark};
  pointer-events: auto;
  animation: ${({ $isExiting }) =>
    $isExiting
      ? css`
          ${toastExit} ${TOAST_EXIT_DURATION}ms ease-in forwards
        `
      : css`
          ${toastEnter} 180ms ease-out
        `};
  will-change: transform, opacity;

  @media (min-width: ${Breakpoints.screen_sm}) {
    min-width: 420px;
  }

  ${({ $variant }) => css`
    background: ${VARIANT_STYLES[$variant].background};
  `}
`;

export const TOAST_CLOSE_RING_RADIUS = 16;
export const TOAST_CLOSE_RING_CIRCUMFERENCE = 2 * Math.PI * TOAST_CLOSE_RING_RADIUS;

const CLOSE_RING_SIZE = 40;

const CloseButtonWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${CLOSE_RING_SIZE}px;
  height: ${CLOSE_RING_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProgressRing = styled.svg`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const ProgressRingIndicator = styled.circle<{ $variant: ToastVariant }>`
  fill: none;
  stroke: ${({ $variant }) => VARIANT_STYLES[$variant].accent};
  stroke-width: 2;
  stroke-linecap: round;
`;

const IconBadge = styled.div<{ $variant: ToastVariant }>`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  color: ${({ $variant }) => VARIANT_STYLES[$variant].accent};
  user-select: none;
`;

const VariantIcon = styled.i`
  font-size: 18px;
`;

const CloseIcon = styled.i`
  font-size: 18px;
  line-height: 1;
`;

const Message = styled.div<{ $variant: ToastVariant }>`
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;

  strong,
  b {
    color: ${({ $variant }) => VARIANT_STYLES[$variant].strong};
    font-weight: ${Type.Weight.semibold};
  }
`;

const CloseButton = styled.button.attrs({
  'aria-label': 'close notification',
  type: 'button',
})<{ $variant: ToastVariant }>`
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  padding: 0;
  color: ${({ $variant }) => VARIANT_STYLES[$variant].accent};
  opacity: 0.65;
  background: transparent;
  border: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

export type AlertToastProps = {
  type: AlertToastType;
  message: React.ReactNode;
  onClose: () => void;
  isExiting?: boolean;
  showProgress?: boolean;
  /** Autohide ring inputs (required when `showProgress` is true). */
  progressDuration?: number;
  progressRemaining?: number;
  progressPaused?: boolean;
  /** Bumps when resuming from pause so the ring animation restarts. */
  progressKey?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export type AlertToastViewportProps = {
  children: React.ReactNode;
};

export function AlertToastViewport({ children }: AlertToastViewportProps): React.ReactElement {
  return (
    <ViewportAnchored>
      <ViewportAlign>{children}</ViewportAlign>
    </ViewportAnchored>
  );
}

export const TOAST_EXIT_MS = TOAST_EXIT_DURATION;

export function AlertToast({
  type,
  message,
  onClose,
  isExiting = false,
  showProgress = false,
  progressDuration,
  progressRemaining,
  progressPaused,
  progressKey,
  onMouseEnter,
  onMouseLeave,
}: AlertToastProps): React.ReactElement {
  const variant = resolveToastVariant(type);
  const [progressStyle, setProgressStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!showProgress || progressDuration === undefined || progressDuration === -1) {
      setProgressStyle({});
      return;
    }

    const C = TOAST_CLOSE_RING_CIRCUMFERENCE;

    if (isExiting) {
      setProgressStyle({
        strokeDashoffset: C,
        transition: 'none',
      });
      return;
    }

    const durationMs = Math.max(1, progressDuration);
    const remaining = progressRemaining ?? 0;
    const fraction = Math.max(0, remaining / durationMs);
    setProgressStyle({
      strokeDashoffset: C * (1 - fraction),
      transition: 'none',
    });

    if (progressPaused) return;

    const frame = window.requestAnimationFrame(() => {
      setProgressStyle({
        strokeDashoffset: C,
        transition: `stroke-dashoffset ${remaining}ms linear`,
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [isExiting, showProgress, progressDuration, progressRemaining, progressPaused, progressKey]);

  return (
    <Banner
      data-qa-hook="reactBanner"
      $isExiting={isExiting}
      $variant={variant}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <IconBadge $variant={variant} aria-hidden>
        <VariantIcon className={toastIconClassName(variant)} />
      </IconBadge>
      <Message $variant={variant}>{message}</Message>
      {showProgress ? (
        <CloseButtonWrap>
          <ProgressRing
            data-qa-hook="toastAutohideProgress"
            viewBox={`0 0 ${CLOSE_RING_SIZE} ${CLOSE_RING_SIZE}`}
            width={CLOSE_RING_SIZE}
            height={CLOSE_RING_SIZE}
            aria-hidden
          >
            <ProgressRingIndicator
              transform={`rotate(-90 ${CLOSE_RING_SIZE / 2} ${CLOSE_RING_SIZE / 2})`}
              cx={CLOSE_RING_SIZE / 2}
              cy={CLOSE_RING_SIZE / 2}
              r={TOAST_CLOSE_RING_RADIUS}
              $variant={variant}
              strokeDasharray={TOAST_CLOSE_RING_CIRCUMFERENCE}
              style={{ ...progressStyle, stroke: VARIANT_STYLES[variant].accent }}
            />
          </ProgressRing>
          <CloseButton $variant={variant} onClick={onClose}>
            <CloseIcon className="fas fa-times" aria-hidden />
          </CloseButton>
        </CloseButtonWrap>
      ) : (
        <CloseButton $variant={variant} onClick={onClose}>
          <CloseIcon className="fas fa-times" aria-hidden />
        </CloseButton>
      )}
    </Banner>
  );
}
